import { useEffect, useMemo, useState } from "react";
import { useForm, useNotification } from "@/hooks";
import { Content } from "@/layout";
import { Icon } from "@/components";
import {
    Badge,
    Button,
    Heading,
    LoadingProgress,
    MessageBanner,
    Panel,
    Select,
    Table,
    TableFooter,
    TableToolbar,
    Text,
} from "@/ui";
import { TreezObjectModal } from "./components";
import { useStoreContext } from "context";
import placeholder from "../../assets/images/placeholder.png";
import { NOT_AVAILABLE } from "@/config";
import {
    OpenSearchProductsProvider,
    useOpenSearchProducts,
    useOpenSearchStoreReadiness,
    getOpenSearchStoreStatusMessage,
} from "@/opensearch";
import styles from "./Treez.module.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 75, 100];

const TreezCatalogBody = ({
    inputs,
    selectChange,
    providers,
    storeAppsLoading,
    treezSellConfigsLoading,
    storeReadinessMessage,
    hasOpenSearchIds,
}) => {
    const notify = useNotification();
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {
        products,
        totalHits,
        searchName,
        setSearchName,
        categoryFilter,
        setCategoryFilter,
        brandFilter,
        setBrandFilter,
        statusFilter,
        setStatusFilter,
        categoryOptions,
        brandOptions,
        clearCatalogFilters,
        page,
        setPage,
        pageSize,
        setPageSize,
        isLoading,
        isFetching,
        isError,
        error,
    } = useOpenSearchProducts();

    const storeOptions = useMemo(
        () =>
            (providers || []).map(provider => ({
                value: String(provider.value),
                label: provider.label,
            })),
        [providers],
    );

    const categoryMenuItems = useMemo(
        () => [
            {
                id: "all-categories",
                label: "All categories",
                onSelect: () => setCategoryFilter(null),
            },
            ...categoryOptions.map(category => ({
                id: `category-${category}`,
                label: category,
                onSelect: () => setCategoryFilter(category),
            })),
        ],
        [categoryOptions, setCategoryFilter],
    );

    const brandMenuItems = useMemo(
        () => [
            {
                id: "all-brands",
                label: "All brands",
                onSelect: () => setBrandFilter(null),
            },
            ...brandOptions.map(brand => ({
                id: `brand-${brand}`,
                label: brand,
                onSelect: () => setBrandFilter(brand),
            })),
        ],
        [brandOptions, setBrandFilter],
    );

    const statusMenuItems = useMemo(
        () => [
            {
                id: "status-yes",
                label: "Yes",
                onSelect: () => setStatusFilter(true),
            },
            {
                id: "status-no",
                label: "No",
                onSelect: () => setStatusFilter(false),
            },
        ],
        [setStatusFilter],
    );

    const toolbarFilters = useMemo(
        () => [
            {
                id: "f-category",
                label: categoryFilter
                    ? `Category · ${categoryFilter}`
                    : "Category",
                active: Boolean(categoryFilter),
                menuItems: categoryFilter ? undefined : categoryMenuItems,
                onRemove: categoryFilter
                    ? () => setCategoryFilter(null)
                    : undefined,
            },
            {
                id: "f-inStock",
                label:
                    statusFilter === null
                        ? "In Stock"
                        : `In Stock · ${statusFilter ? "Yes" : "No"}`,
                active: statusFilter !== null,
                menuItems: statusFilter !== null ? undefined : statusMenuItems,
                onRemove:
                    statusFilter !== null
                        ? () => setStatusFilter(null)
                        : undefined,
            },
            {
                id: "f-brand",
                label: brandFilter
                    ? `Brand Name · ${brandFilter}`
                    : "Brand Name",
                active: Boolean(brandFilter),
                menuItems: brandFilter ? undefined : brandMenuItems,
                onRemove: brandFilter ? () => setBrandFilter(null) : undefined,
            },
        ],
        [
            categoryFilter,
            statusFilter,
            brandFilter,
            categoryMenuItems,
            statusMenuItems,
            brandMenuItems,
            setCategoryFilter,
            setStatusFilter,
            setBrandFilter,
        ],
    );

    const hasActiveFilters =
        Boolean(categoryFilter) ||
        Boolean(brandFilter) ||
        statusFilter !== null ||
        Boolean(searchName.trim());

    const clipboardCopy = item => {
        navigator.clipboard.writeText(item);
        notify(`Product Id:${item} is copied`, "success");
    };

    const columns = useMemo(
        () => [
            {
                id: "assets",
                header: "Image",
                width: 100,
                renderCell: row => {
                    const cell = row.assets;
                    const imageURL =
                        Array.isArray(cell) && typeof cell[0] === "string"
                            ? cell[0]
                            : placeholder;
                    const hasImage = Array.isArray(cell) && Boolean(cell[0]);

                    return (
                        <div className={styles.imageCell}>
                            <img
                                alt={row.name}
                                className={styles.image}
                                key={`${row.id}-${imageURL}`}
                                onError={event => {
                                    event.currentTarget.src = placeholder;
                                }}
                                src={imageURL}
                            />
                            {!hasImage ? (
                                <span className={styles.noImage}>No image</span>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                id: "name",
                header: "Name",
                minWidth: 180,
                renderCell: row => (
                    <div className={styles.primaryCell}>
                        {row.name || NOT_AVAILABLE}
                    </div>
                ),
            },
            {
                id: "brand",
                header: "Brand Name",
                minWidth: 140,
                renderCell: row => (
                    <div className={styles.secondaryCell}>
                        {row.brand || NOT_AVAILABLE}
                    </div>
                ),
            },
            {
                id: "category",
                header: "Category",
                minWidth: 120,
                renderCell: row => (
                    <div className={styles.secondaryCell}>
                        {row.category || NOT_AVAILABLE}
                    </div>
                ),
            },
            {
                id: "isActive",
                header: "In Stock",
                minWidth: 100,
                renderCell: row => (
                    <Badge variant={row.isActive ? "primary" : "neutral"}>
                        {row.isActive ? "Yes" : "No"}
                    </Badge>
                ),
            },
            {
                id: "availableUnits",
                header: "Quantity",
                minWidth: 100,
                renderCell: row => (
                    <div className={styles.primaryCell}>
                        {row.availableUnits || 0}
                    </div>
                ),
            },
            {
                id: "id",
                header: "Product Id",
                minWidth: 140,
                renderCell: row => (
                    <span
                        className={styles.copyAction}
                        onClick={event => event.stopPropagation()}
                    >
                        <Button
                            iconRight={<Icon color="#727a94" icon="copy" />}
                            onClick={() => clipboardCopy(row.id)}
                            size="sm"
                            title="Copy Product Id"
                            type="button"
                            variant="tertiary"
                        >
                            Copy ID
                        </Button>
                    </span>
                ),
            },
        ],
        [],
    );

    const tableLoading =
        isLoading ||
        isFetching ||
        storeAppsLoading ||
        treezSellConfigsLoading ||
        (Boolean(inputs.store) && !hasOpenSearchIds);

    const totalPages = Math.max(1, Math.ceil(totalHits / pageSize) || 1);
    const currentPage = Math.min(page + 1, totalPages);

    return (
        <>
            <Content>
                <Panel
                    className={styles.root}
                    header={
                        <div className={styles.headerBar}>
                            <div className={styles.headerText}>
                                <Heading level={4}>Products</Heading>
                                <Text tone="secondary" variant="body-md">
                                    All Treez Products available in the system
                                </Text>
                            </div>
                            <Select
                                className={styles.storeSelect}
                                fullWidth
                                label="Store"
                                onValueChange={value =>
                                    selectChange({ value }, "store")
                                }
                                options={storeOptions}
                                value={
                                    inputs.store
                                        ? String(inputs.store)
                                        : undefined
                                }
                            />
                        </div>
                    }
                    showHeaderDivider
                >
                    {(storeReadinessMessage || (isError && error)) && (
                        <div className={styles.banners}>
                            {storeReadinessMessage ? (
                                <MessageBanner
                                    aria-label="Store readiness"
                                    description={storeReadinessMessage}
                                    role="status"
                                />
                            ) : null}
                            {isError && error ? (
                                <MessageBanner
                                    aria-label="OpenSearch error"
                                    description={
                                        <>
                                            <strong>OpenSearch:</strong>{" "}
                                            {error?.message ?? "Request failed"}
                                        </>
                                    }
                                    role="alert"
                                />
                            ) : null}
                        </div>
                    )}

                    <TableToolbar
                        count={totalHits}
                        filters={toolbarFilters}
                        label="Products"
                        mode={
                            hasActiveFilters ? "active-filters" : "filterable"
                        }
                        onClearAllFilters={clearCatalogFilters}
                        onSearchChange={value => {
                            setSearchName(value ?? "");
                        }}
                        searchable
                        searchValue={searchName}
                        showMoreFilters={false}
                        updatedAtLabel=""
                    />

                    {tableLoading ? (
                        <div className={styles.loading}>
                            <LoadingProgress
                                indeterminate
                                indicator="circular"
                                size="sm"
                                title="Loading"
                            />
                        </div>
                    ) : (
                        <>
                            <Table
                                columns={columns}
                                emptyMessage="No products found"
                                getRowId={row => String(row.id)}
                                onRowClick={row => {
                                    setSelectedProduct(row);
                                    setIsModalOpen(true);
                                }}
                                rows={products}
                            />
                            <TableFooter
                                onPageChange={nextPage => {
                                    setPage(Math.max(0, nextPage - 1));
                                }}
                                onPageSizeChange={size => {
                                    setPageSize(size);
                                    setPage(0);
                                }}
                                page={currentPage}
                                pageSize={pageSize}
                                pageSizeOptions={PAGE_SIZE_OPTIONS}
                                totalPages={totalPages}
                                totalRows={totalHits}
                            />
                        </>
                    )}
                </Panel>
            </Content>
            <TreezObjectModal
                handleModalToggle={() => setIsModalOpen(false)}
                isModalOpen={isModalOpen}
                object={selectedProduct}
            />
        </>
    );
};

const TreezPage = () => {
    const { providers, storeAppsLoading } = useStoreContext();
    const { inputs, selectChange, setInputs } = useForm();
    const {
        isLoading: treezSellConfigsLoading,
        readiness,
        isReady,
    } = useOpenSearchStoreReadiness(inputs.store);

    useEffect(() => {
        if (!providers?.length || inputs.store) {
            return;
        }

        setInputs({
            ...inputs,
            store: providers[0]?.value || null,
        });
    }, [providers, inputs, setInputs]);

    const storeReadinessMessage = useMemo(() => {
        if (!inputs.store || treezSellConfigsLoading) {
            return null;
        }

        if (isReady) {
            return null;
        }

        return getOpenSearchStoreStatusMessage(readiness);
    }, [inputs.store, treezSellConfigsLoading, isReady, readiness]);

    const hasOpenSearchIds = isReady;

    return (
        <OpenSearchProductsProvider
            key={inputs.store ?? "no-store"}
            store={readiness?.status === "ready" ? readiness.store : null}
        >
            <TreezCatalogBody
                hasOpenSearchIds={hasOpenSearchIds}
                inputs={inputs}
                providers={providers}
                selectChange={selectChange}
                storeAppsLoading={storeAppsLoading}
                storeReadinessMessage={storeReadinessMessage}
                treezSellConfigsLoading={treezSellConfigsLoading}
            />
        </OpenSearchProductsProvider>
    );
};

export default TreezPage;
